import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Sparkline } from '../Chart/Sparkline';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { Popover } from '../Popover/Popover';
import { STATUS_INK, type StatusRole } from '../StatusSymbol/StatusSymbol';

/**
 * THE ONE TILE (DESIGN.md B1: one part per visual thing; redesign register D9, D10, L4, L7, L9,
 * R-K1; brief FE-FOUNDATION section 6; components page section 3; mockup
 * dashboard-v2-side.html:147-174). It replaces `KpiTile` and `StatTile`, which the 4 Oct
 * root-cause analysis counted as one visual thing built twice (BR-PARTS).
 *
 * Grey, because the tile row is one of the two places L4 allows a grey fill (the kit's group
 * box, `--color-kpi-tile`). A leading mark, the label at 13 in the secondary ink, the number
 * at 34 for the lead tile and 22 for the others (L7, the part's two sizes), a note line at 13
 * whose delta part says whether things got better or worse since last week, and a neutral
 * sparkline (R-K1).
 *
 * THREE MODES. `pick`: the tile is a button; two pick tiles form one single-choice pair whose
 * selection the caller owns (the Dashboard's Needs attention and Keep an eye on, which filter
 * the list below), and the selected one wears a neutral dark border, no blue ring (L9).
 * `link`: the whole tile opens a page, with the chevron at the trailing top.
 * `static` (added by BR-INTAKE for the intake's key facts and by BR-CLIENTPAGE for the client
 * page's Revenue, New subs and Subs growth): a fact or measured quantity that opens nothing, so
 * it carries no control, no chevron, no hover tint and no focus ring - DESIGN.md B11, "never
 * make a tile that is not clickable a link", and A24, a quantity whose value and change matter
 * is a tile. Added beside the two modes, so every `pick` and `link` caller renders exactly as
 * before (B3).
 *
 * THE CONTROL COVERS THE TILE AND THE NOTE STAYS OUTSIDE IT. The button or link is stretched
 * over the whole tile, so a click anywhere picks or opens it, while the note line sits above
 * that layer: "+N since last week" opens the names behind the number in a popover (open
 * question Q-FND-1, decided A), and a button cannot sit inside a button or a link.
 *
 * A VALUE THE CALLER MAY NOT READ is an em dash and never a zero, and draws no sparkline:
 * "nothing needs attention" and "you may not see this" are not the same sentence, and a trend
 * line beside the dash would show the shape of the number it just refused. Numbers in the
 * system face with PROPORTIONAL figures (D19: tabular figures are for columns that must align,
 * not a standalone number; the mockups' rounded face is the components page's open note,
 * Part 6 Q12).
 *
 * HELD WHILE IT RE-READS (dashboard.md D20, carried over from `StatTile`): with `refetching`
 * the previous render stays at reduced opacity and nothing moves - a row of tiles that
 * flashed skeletons every time somebody ticked a checkbox would read as broken. No press
 * scale and no counting up: a data tile that squishes reads as a button, and a number that
 * animates cannot be read while it does.
 */
interface TileBase {
  /** Sentence case, no colon. */
  readonly label: string;
  /** A number, a preformatted string (money: "EUR 35,250"), or null for the em dash. */
  readonly value: number | string | null;
  /** The part's two sizes (L7): the lead tile takes the 34 number; every other tile 22. */
  readonly lead?: boolean | undefined;
  /** Colours the number in a status role (the lead tile's act-now red, components page). */
  readonly valueTone?: StatusRole | undefined;
  /** Before the label: a `StatusSymbol`, or a caller's own mark. */
  readonly mark?: ReactNode | undefined;
  /**
   * The signed change the note line starts with. An increase reads in the act-now tone and a
   * decrease in the done tone (brief section 6); zero has no tone.
   */
  readonly delta?: number | undefined;
  /** The rest of the note line: "since last week", "8 payments · oldest 74 days". */
  readonly note?: ReactNode | undefined;
  /**
   * What "+N since last week" opens: the names behind the change (D10, Q-FND-1 A). When
   * given, the delta and note become a text action opening a popover with this content.
   */
  readonly changes?: ReactNode | undefined;
  /** The readings behind the number, oldest first; fewer than three draw nothing. */
  readonly sparkline?: readonly (number | null)[] | undefined;
  readonly sparklineLabel?: string | undefined;
  /** Held at the previous value, dimmed, while the query re-reads (D20). */
  readonly refetching?: boolean | undefined;
  readonly className?: string | undefined;
}

/**
 * THE TWO LOOKS (BR-PARTS; DESIGN.md B3). `group`, the default, is the five redesigned pages'
 * tile above. `card` is the look `StatTile` drew before the merge - a white raised card, the
 * label over a 28 px number, the sparkline beside it, the note under it, no chevron - kept
 * class for class because B3 lets a primitive change only additively: the Reports page's big
 * number is outside the five pages and renders exactly as it did. It is a link and nothing
 * else, and it draws no mark, delta, change list, tone or lead size, so the type refuses them.
 */
export type TileLook = 'group' | 'card';

export type TileProps = TileBase &
  (
    | {
        readonly mode: 'pick';
        readonly selected: boolean;
        readonly onSelect: () => void;
        readonly look?: 'group' | undefined;
      }
    | {
        readonly mode: 'link';
        readonly to: string;
        readonly look?: 'group' | undefined;
      }
    | {
        readonly mode: 'static';
        readonly look?: 'group' | undefined;
      }
    | {
        readonly mode: 'link';
        readonly to: string;
        readonly look: 'card';
        readonly lead?: undefined;
        readonly valueTone?: undefined;
        readonly mark?: undefined;
        readonly delta?: undefined;
        readonly changes?: undefined;
      }
  );

/** "+3", "−2" (the true minus sign, as the components page writes it), "0". */
export function signedDelta(delta: number): string {
  if (delta > 0) return `+${delta.toLocaleString('en-US')}`;
  if (delta < 0) return `−${Math.abs(delta).toLocaleString('en-US')}`;
  return '0';
}

/** An increase is worse, a decrease better, on every tile the five pages draw. */
export function deltaTone(delta: number): string | undefined {
  if (delta > 0) return STATUS_INK['act-now'];
  if (delta < 0) return STATUS_INK.done;
  return undefined;
}

/** The control's own layer covers the whole tile; the note sits above it. */
const STRETCHED = "after:absolute after:inset-0 after:rounded-[inherit] after:content-['']";

/**
 * `StatTile`'s rendering as it stood on main 91d5a1d1, unchanged: the same nodes, classes and
 * number format (`toLocaleString()` in the reader's locale), so the callers outside the five
 * pages see no difference (B3).
 */
function CardLook({
  label,
  value,
  note,
  to,
  refetching = false,
  sparkline,
  sparklineLabel,
  className,
}: TileBase & { readonly to: string }) {
  return (
    <Link
      to={to}
      className={cx(
        'flex min-h-(--tile-min-height) flex-col justify-between gap-1',
        'rounded-surface border border-line bg-surface p-3 shadow-raised',
        'transition-[background-color,opacity] duration-(--duration-fast) ease-(--ease-out)',
        'hover:bg-surface-hover',
        refetching && 'opacity-60 duration-(--duration-base)',
        className,
      )}
    >
      <span className="text-2xs font-medium text-ink-muted">{label}</span>
      <span className="flex items-end justify-between gap-2">
        <span className="text-xl font-semibold tracking-tight text-ink">
          {value === null ? (
            <>
              <span aria-hidden="true">&mdash;</span>
              <span className="sr-only">Not available</span>
            </>
          ) : (
            value.toLocaleString()
          )}
        </span>
        {sparkline !== undefined && value !== null && (
          <Sparkline
            points={sparkline}
            {...(sparklineLabel === undefined ? {} : { label: sparklineLabel })}
          />
        )}
      </span>
      {note !== undefined && <span className="text-2xs text-ink-subtle">{note}</span>}
    </Link>
  );
}

export function Tile(props: TileProps) {
  if (props.mode === 'link' && props.look === 'card') return <CardLook {...props} />;
  const {
    label,
    value,
    lead = false,
    valueTone,
    mark,
    delta,
    note,
    changes,
    sparkline,
    sparklineLabel,
    refetching = false,
    className,
  } = props;
  const selected = props.mode === 'pick' && props.selected;

  const head = (
    <>
      <span className="flex items-center gap-1.5 pr-6 text-2xs text-ink-muted">
        {mark}
        <span className="min-w-0">{label}</span>
      </span>
      <span
        className={cx(
          'mt-1 block font-semibold tracking-tight',
          lead ? 'text-2xl' : 'text-lg',
          valueTone === undefined ? 'text-ink' : STATUS_INK[valueTone],
        )}
        data-kpi-value=""
      >
        {value === null ? (
          <>
            <span aria-hidden="true">&mdash;</span>
            <span className="sr-only">Not available</span>
          </>
        ) : typeof value === 'number' ? (
          value.toLocaleString('en-US')
        ) : (
          value
        )}
      </span>
    </>
  );

  const control =
    props.mode === 'static' ? (
      <div data-kpi-main="">{head}</div>
    ) : props.mode === 'pick' ? (
      <button
        type="button"
        aria-pressed={props.selected}
        onClick={props.onSelect}
        data-kpi-main=""
        className={cx('block w-full cursor-default text-left outline-none', STRETCHED)}
      >
        {head}
      </button>
    ) : (
      <Link
        to={props.to}
        data-kpi-main=""
        className={cx('block w-full cursor-default text-left outline-none', STRETCHED)}
      >
        {head}
        <Icon name="chevron-right" className="absolute top-4 right-4 text-ink-subtle" size="1rem" />
      </Link>
    );

  const noteWords =
    delta === undefined && note === undefined ? null : (
      <>
        {delta !== undefined && <span className={deltaTone(delta)}>{signedDelta(delta)}</span>}
        {delta !== undefined && note !== undefined && ' '}
        {note}
      </>
    );

  return (
    <div
      data-kpi-tile={props.mode}
      data-selected={selected ? 'true' : undefined}
      className={cx(
        props.mode !== 'static' && 'focus-ring-within',
        'relative flex min-h-(--tile-min-height) flex-col justify-between gap-2',
        'rounded-(--record-group-radius) bg-(--color-kpi-tile) p-4',
        /* `transition-colors` plus opacity, so the D20 hold fades in on the same curve. */
        'transition-[color,background-color,border-color,outline-color,text-decoration-color,fill,stroke,opacity]',
        'duration-(--duration-fast) ease-(--ease-out)',
        props.mode !== 'static' && 'hover:bg-surface-hover',
        selected && 'ring-2 ring-(--color-tile-selected-border) ring-inset',
        refetching && 'opacity-60 duration-(--duration-base)',
        className,
      )}
    >
      {control}
      <div className="flex items-end justify-between gap-3">
        {noteWords === null ? (
          <span />
        ) : changes === undefined ? (
          <span className="text-2xs text-ink-muted" data-kpi-note="">
            {noteWords}
          </span>
        ) : (
          <span className="relative z-10">
            <Popover
              ariaLabel={label}
              trigger={
                <button
                  type="button"
                  data-kpi-note=""
                  className="hit-expand cursor-default rounded-control text-2xs text-ink-muted hover:underline"
                >
                  {noteWords}
                </button>
              }
            >
              {changes}
            </Popover>
          </span>
        )}
        {sparkline !== undefined && value !== null && (
          <Sparkline
            points={sparkline}
            size="tile"
            {...(sparklineLabel === undefined ? {} : { label: sparklineLabel })}
          />
        )}
      </div>
    </div>
  );
}
